import { Assert } from '..';

/**
 * Returns whether the set of indices form a contiguous slice, and if so
 * provides the begin index and exclusive end index.
 *
 * e.g.
 * ```TypeScript
 * function getRows(indices: number[], table: Table) {
 *   const continuity = isContiguousSlice(indices);
 *   if (continuity.isConiguous) {
 *     return table.slice(continuity.begin, continuity.end);  // fast operation
 *   } else {
 *     return table.take(indices);  // slow operation
 *   }}
 * }
 * ```
 *
 * Performance:
 * - Slowish O(n log n) time complexity (in exchange for straightforward implementation).
 * - An O(n) solution exists.
 */
export function isContiguousSlice(
  indices: number[]
):
  | { isContiguous: true; beginInclusive: number; endExclusive: number }
  | { isContiguous: false } {
  if (indices.length === 0) return { isContiguous: false };

  // Sort the array
  const sorted = [...new Set(indices)].sort((a, b) => a - b);

  // Check if each element is consecutive
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] !== Assert.assertExists(sorted[i - 1]) + 1) {
      return { isContiguous: false };
    }
  }

  return {
    isContiguous: true,
    beginInclusive: Assert.assertExists(sorted[0]),
    endExclusive: Assert.assertExists(sorted[sorted.length - 1]) + 1,
  };
}
